import React, { useState, useCallback, useRef } from 'react';
import { Mention, MentionsInput } from 'react-mentions';
import Suggestions from '../../../utils/Suggestions';
import { checkAuth, useAuthInfo } from '../../../../lib/jwt';
import { EntityType } from '../../../../types/dto/EntityType';
import { CommentDtoType } from '../../../../types/dto/CommentDtoType';
import { CommentContentInput } from '../../../plaza/basic/CommentContentInput';
import { ImageDataType } from '../../../../types/ImageDataType';
import { dataURLtoFile } from '../../../../lib/fileHelper';
import { IconButton } from '../../../basic/iconButton';
import GiphyComponent from '../../../plaza/comments/GiphyComponent';
import { toast } from 'react-toastify';
import { useRouter } from 'next/router';
import { ASSETS_BASE_URL } from '../../../../lib/constants';
import logCustomEvent from '../../../../helpers/analytics';

type CommentInputProps = {
  className: string;
  linkedId: number;
  parentId?: number;
  type: EntityType;
  onSubmitted: (comment: CommentDtoType) => void;
  onShowReplies?: (value: boolean) => void;
  disabled?: boolean;
};

const CommentInput = ({
  className,
  linkedId,
  parentId,
  type,
  onSubmitted,
  onShowReplies,
  disabled
}: CommentInputProps) => {
  const [text, setText] = useState<string>('');
  const [displayText, setDisplayText] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [authInfo, token] = useAuthInfo(false);
  const [commentImage, setCommentImage] = useState<ImageDataType>();
  const imageInputRef = useRef<HTMLInputElement>();
  const [showGiphy, setShowGiphy] = useState(false);
  const [giphyImg, setGiphyImg] = useState<string>('');
  const router = useRouter();

  const GiphyHandel = useCallback((link: string) => {
    setGiphyImg(link);
    setShowGiphy(false);
  }, []);

  const attachImageCallback = useCallback(() => {
    if (imageInputRef.current) {
      imageInputRef.current.click();
    }
  }, [commentImage]);

  const imageSelectedCallback = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    e => {
      if (e.target.files?.length > 0) {
        const file = e.target.files[0];
        if (
          file.type === 'image/jpeg' ||
          file.type === 'image/jpg' ||
          file.type === 'image/png' ||
          file.type === 'image/gif'
        ) {
          setCommentImage({
            imageFile: file
          });
        }
        e.target.value = '';
      }
    },
    []
  );

  const submitComment = useCallback(
    async (
      text: string,
      linkedId: number,
      userId: number,
      commentImage: ImageDataType,
      parentId: number,
      giphyImg: string
    ) => {
      if (!text || text.trim() === '') {
        toast.error('Text field is required', {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined
        });
        return;
      };

      const data = new FormData();
      data.set('userId', `${userId}`);
      data.set('text', text);
      data.set('linkedId', `${linkedId}`);

      if (parentId) {
        data.set('parentId', `${parentId}`);
      }

      if (giphyImg) {
        data.set('animatedImage', giphyImg);
      }

      if (!giphyImg) {
        if (commentImage) {
          let imageFile = commentImage.imageFile;

          if (commentImage.croppedImage) {
            imageFile = dataURLtoFile(
              commentImage.croppedImage,
              commentImage.imageFile.name
            );
          }

          data.set('image', imageFile);
        }
      }

      let newComment: CommentDtoType;
      try {
        setIsSubmitting(true);
        // analytics
        logCustomEvent('PlazaComment', {
          user_id: userId,
          text: text,
          linked_id: linkedId,
          parent_id: parentId,
        });
        const response = await fetch(`/api/comments/${type}`, {
          method: 'POST',
          mode: 'cors',
          cache: 'no-cache',
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: data
        });

        if (response.status === 200) {
          newComment = (await response.json()) as CommentDtoType;
        } else {
          toast.error('Something went wrong, or image contains inappropriate content', {
            position: 'top-center',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined
          });
          setCommentImage(null);
        }
      } catch (error) {
        toast.error('Something went wrong, please try again later', {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined
        });
      } finally {
        setIsSubmitting(false);

        if (newComment) {
          setText('');
          setCommentImage(null);
          setGiphyImg('');
          onSubmitted(newComment);
        }

        if (parentId) onShowReplies(true);
      }
    },
    [text, type, commentImage, giphyImg]
  );

  const keyPressHandler = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.altKey) {
      checkAuth(
        () =>
          submitComment(text, linkedId, authInfo?.id, commentImage, parentId, giphyImg),
        router
      );
    }
  };

  const fetchUsers = async (query, callback) => {
    if (!query) return;
    try {
      const response = await fetch(`/api/users/${query}`, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (response.status === 200) {
        response.json().then(result => {
          console.log(result.users);
          let actualUsers = [];
          for (let user of result?.users) {
            actualUsers.push({
              id: user.id,
              display: '@' + user.username,
              avatar: user.avatar
            });
          }
          callback(actualUsers);
        });
      }
    } catch (error) {
      toast.error('Something went wrong, please try again later', {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined
      });
    }
  };

  const onChange = useCallback(
    (event, newValue, plainTextValue) => {
      setText(plainTextValue.slice(0, 250));
      setDisplayText(newValue.slice(0, 250));
    },
    [setText, setDisplayText]
  );

  const renderSuggestion = entry => {
    return <Suggestions display={entry.display} avatar={entry.avatar} />;
  };

  return (
    <div className={`flex flex-col relative ${className || ''}`}>
      <MentionsInput
        className="mentionWrapper absolute resize-none"
        value={text}
        maxLength={255}
        onChange={onChange}
        disabled={isSubmitting}
        placeholder={!parentId ? 'Add a comment...' : 'Add a reply...'}
        onKeyPress={keyPressHandler}
      >
        <Mention
          trigger="@"
          data={fetchUsers}
          className="mentionedFriend"
          renderSuggestion={renderSuggestion}
        ></Mention>
      </MentionsInput>
      {giphyImg && (
        <div className="mt-2.5 relative">
          <>
            <img src={giphyImg} className="h-36 w-52" />
            <div
              onClick={() => {
                setGiphyImg(null);
              }}
              className=" absolute left-44 top-0"
            >
              <img src="/assets/icons/close-giphy.svg"></img>
            </div>
          </>
        </div>
      )}
      <CommentContentInput
        attachedImage={commentImage}
        disabled={isSubmitting}
        onAttachedImageChange={image => setCommentImage(image)}
        onChangeAttachedImageRequest={attachImageCallback}
      />
      <div className="flex absolute right-8 top-2 items-center space-x-2">
        <input
          ref={imageInputRef}
          type="file"
          className="hidden"
          accept=".jpg, .jpeg, .png"
          onChange={imageSelectedCallback}
        />
        <IconButton
          onClick={attachImageCallback}
          disabled={isSubmitting || !!commentImage || !!giphyImg}
          tooltip={
            !commentImage && !giphyImg
              ? 'Add a picture'
              : 'You can add just 1 image / GIF to the comment'
          }
          tooltipPlacement="top"
        >
          <img
            className="cursor-pointer text-lg text-white"
            src={`${ASSETS_BASE_URL}system/cw-assets/icon-img.svg`}
          />
        </IconButton>
        <div>
          <IconButton
            onClick={() => {
              setShowGiphy(!showGiphy);
            }}
            disabled={isSubmitting || !!giphyImg || !!commentImage}
            tooltip={
              !giphyImg && !commentImage
                ? 'Add a GIF'
                : 'You can add just 1 image / GIF to the comment'
            }
            tooltipPlacement="top"
          >
            <img
              className="cursor-pointer text-lg text-white"
              src={`${ASSETS_BASE_URL}system/cw-assets/icon-gif.svg`}
            />
          </IconButton>
        </div>
      </div>
      {showGiphy ? <GiphyComponent onHandleClick={GiphyHandel} /> : null}
    </div>
  );
};

export default CommentInput;
